import { useState, useEffect } from 'react';
import { 
  CalendarCheck, 
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getReservations, updateReservationStatus, deleteReservation, getZones, getHotels, getExcursions, getVehicles } from '../lib/api';

export default function AdminReservations() {
  const [reservations, setReservations] = useState([]);
  const [zones, setZones] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchData = async () => {
    try {
      const [resRes, zonesRes, hotelsRes, excursionsRes, vehiclesRes] = await Promise.all([
        getReservations(),
        getZones(),
        getHotels(),
        getExcursions(),
        getVehicles()
      ]);
      setReservations(resRes.data);
      setZones(zonesRes.data);
      setHotels(hotelsRes.data);
      setExcursions(excursionsRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading reservations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getZoneName = (id) => zones.find(z => z.id === id)?.name || '-';
  const getHotelName = (id) => hotels.find(h => h.id === id)?.name || '-';
  const getExcursionName = (id) => excursions.find(e => e.id === id)?.name || '-';
  const getVehicleName = (id) => vehicles.find(v => v.id === id)?.name || '-';

  const handleStatusChange = async (id, status) => {
    try {
      await updateReservationStatus(id, status);
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteReservation(deleteId);
      toast.success('Reservation deleted');
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error('Error deleting reservation');
    }
  };

  const filteredReservations = filter === 'all' 
    ? reservations 
    : reservations.filter(r => r.status === filter);

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    confirmed: 'bg-green-500/20 text-green-400',
    completed: 'bg-cyan-500/20 text-cyan-400',
    cancelled: 'bg-red-500/20 text-red-400'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading reservations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reservations">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-white lg:hidden">Reservations</h1>
        
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status 
                ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50' 
                : 'border-white/10 text-slate-400'}
              data-testid={`filter-${status}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              <Badge variant="secondary" className="ml-2 bg-white/10">
                {status === 'all' 
                  ? reservations.length 
                  : reservations.filter(r => r.status === status).length}
              </Badge>
            </Button>
          ))}
        </div>
      </div>

      {/* Reservations List */}
      <div className="space-y-4">
        {filteredReservations.length === 0 ? (
          <div className="admin-card p-12 text-center">
            <CalendarCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No reservations found</p>
          </div>
        ) : (
          filteredReservations.map((res) => (
            <div 
              key={res.id} 
              className="admin-card p-4"
              data-testid={`reservation-${res.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-white">{res.name}</h3>
                    <Badge className={statusColors[res.status] || statusColors.pending}>
                      {res.status}
                    </Badge>
                    <Badge variant="outline" className="border-white/10 text-slate-400">
                      {res.reservation_type}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {res.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {res.whatsapp}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {res.date} at {res.time}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-400 hover:text-white"
                    onClick={() => setSelectedReservation(res)}
                    data-testid={`view-reservation-${res.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  {res.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-green-400 hover:text-green-300"
                        onClick={() => handleStatusChange(res.id, 'confirmed')}
                        data-testid={`confirm-reservation-${res.id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleStatusChange(res.id, 'cancelled')}
                        data-testid={`cancel-reservation-${res.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {res.status === 'confirmed' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-cyan-400 hover:text-cyan-300"
                      onClick={() => handleStatusChange(res.id, 'completed')}
                      data-testid={`complete-reservation-${res.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-400 hover:text-red-300"
                    onClick={() => setDeleteId(res.id)}
                    data-testid={`delete-reservation-${res.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* View Dialog */}
      <Dialog open={!!selectedReservation} onOpenChange={() => setSelectedReservation(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Reservation Details</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Name</p>
                  <p className="font-medium">{selectedReservation.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge className={statusColors[selectedReservation.status]}>
                    {selectedReservation.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Email</p>
                  <p className="font-medium">{selectedReservation.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">WhatsApp</p>
                  <p className="font-medium">{selectedReservation.whatsapp}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Date</p>
                  <p className="font-medium">{selectedReservation.date}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Time</p>
                  <p className="font-medium">{selectedReservation.time}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Passengers</p>
                  <p className="font-medium">{selectedReservation.passengers}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Type</p>
                  <p className="font-medium">{selectedReservation.reservation_type}</p>
                </div>
                {selectedReservation.reservation_type === 'transfer' && (
                  <>
                    <div>
                      <p className="text-sm text-slate-400">Pickup</p>
                      <p className="font-medium">{selectedReservation.pickup_location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Zone</p>
                      <p className="font-medium">{getZoneName(selectedReservation.zone_id)}</p>
                    </div>
                    {selectedReservation.hotel_id && (
                      <div className="col-span-2">
                        <p className="text-sm text-slate-400">Hotel</p>
                        <p className="font-medium">{getHotelName(selectedReservation.hotel_id)}</p>
                      </div>
                    )}
                    {selectedReservation.trip_type && (
                      <div>
                        <p className="text-sm text-slate-400">Modalidad</p>
                        <p className="font-medium">
                          {selectedReservation.trip_type === 'round_trip' ? (
                            <span className="text-orange-400">Ida y Vuelta</span>
                          ) : 'Solo Ida'}
                        </p>
                      </div>
                    )}
                    {selectedReservation.trip_type === 'round_trip' && selectedReservation.return_date && (
                      <div>
                        <p className="text-sm text-slate-400">Fecha Regreso</p>
                        <p className="font-medium">{selectedReservation.return_date} {selectedReservation.return_time || ''}</p>
                      </div>
                    )}
                  </>
                )}
                {selectedReservation.reservation_type === 'excursion' && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-400">Excursion</p>
                    <p className="font-medium">{getExcursionName(selectedReservation.excursion_id)}</p>
                  </div>
                )}
                {selectedReservation.vehicle_id && (
                  <div>
                    <p className="text-sm text-slate-400">Vehicle</p>
                    <p className="font-medium">{getVehicleName(selectedReservation.vehicle_id)}</p>
                  </div>
                )}
                {selectedReservation.total_price && (
                  <div>
                    <p className="text-sm text-slate-400">Total Price</p>
                    <p className="font-medium text-green-400">${selectedReservation.total_price}</p>
                  </div>
                )}
              </div>
              {selectedReservation.comments && (
                <div>
                  <p className="text-sm text-slate-400">Comments</p>
                  <p className="font-medium">{selectedReservation.comments}</p>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <a
                  href={`https://wa.me/${selectedReservation.whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-green-600 hover:bg-green-700">
                    <Phone className="w-4 h-4 mr-2" />
                    Contact via WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Reservation?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This action cannot be undone. The reservation will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
